import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const STATUS_MAP = {
  sent:    { label: 'Sent',    color: '#10B981', bg: '#D1FAE5', icon: CheckCircleIcon },
  failed:  { label: 'Failed',  color: '#EF4444', bg: '#FEE2E2', icon: XCircleIcon },
  pending: { label: 'Pending', color: '#F59E0B', bg: '#FEF3C7', icon: ClockIcon },
};

const TYPE_LABELS = {
  welcome_student:          'Student Welcome',
  placement_result:         'Placement Result',
  lesson_complete:          'Lesson Complete',
  certificate_issued:       'Certificate Issued',
  level_exam_result:        'Level Exam',
  trial_expiring:           'Trial Expiring',
  tutor_application_received: 'Tutor Application',
  tutor_approved:           'Tutor Approved',
  tutor_rejected:           'Tutor Rejected',
  tutor_new_student:        'Tutor New Student',
  tutor_lesson_scheduled:   'Lesson Scheduled',
  tutor_payment_sent:       'Tutor Payment',
  tutor_monthly_statement:  'Monthly Statement',
  tenant_welcome:           'Partner Welcome',
  tenant_payment_confirmed: 'Partner Payment',
  tenant_payment_reminder:  'Payment Reminder',
  tenant_student_trial_started: 'Trial Started',
  tenant_tutor_applied:     'Tutor Applied',
  tenant_api_limit_warning: 'API Warning',
  tenant_api_limit_reached: 'API Limit',
  admin_new_tenant:         'New Partner',
  admin_payment_received:   'Payment Alert',
  admin_api_cost_spike:     'Cost Spike',
  admin_system_error:       'System Error',
};

export default function EmailLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(true);

  // API key saving
  const [showKeyPanel, setShowKeyPanel] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [savingKey, setSavingKey] = useState(false);

  const checkEmailConfig = useCallback(async () => {
    try {
      const res = await api.get('/api/superadmin/email-config-status');
      setEmailConfigured(res.data.configured === true);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { checkEmailConfig(); }, [checkEmailConfig]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/superadmin/email-logs?page=${page}&per_page=50`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (typeFilter) url += `&type=${typeFilter}`;
      const res = await api.get(url);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const sendTest = async () => {
    if (!testEmail) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) { toast.error('Please enter a valid email address (e.g. hello@speakiq.uk)'); return; }
    setSending(true);
    try {
      const res = await api.post('/api/superadmin/email-test', { recipient: testEmail });
      if (res.data.success) {
        toast.success(`Test email sent to ${testEmail}! Check your inbox.`);
        setEmailConfigured(true);
        setTimeout(fetchLogs, 2000);
      } else {
        toast.error(res.data.error || 'Email failed — check your Resend API key');
      }
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed to send — check your Resend API key';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const saveApiKey = async () => {
    if (!apiKeyInput.trim()) return toast.error('Enter a valid API key');
    setSavingKey(true);
    try {
      await api.post('/api/superadmin/settings/email-key', { api_key: apiKeyInput.trim() });
      toast.success('Resend API key saved and activated!');
      setShowKeyPanel(false);
      setApiKeyInput('');
      setEmailConfigured(true);
    } catch (e) {
      toast.error('Failed to save API key: ' + (e.response?.data?.error || e.message));
    } finally {
      setSavingKey(false);
    }
  };

  const sentCount   = logs.filter(l => l.status === 'sent').length;
  const failedCount = logs.filter(l => l.status === 'failed').length;

  return (
    <Layout>
      <div style={{ padding: '32px 40px', background: '#F0F7FA', minHeight: '100vh' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0D1B2A', margin: 0 }}>Email Logs</h1>
            <p style={{ color: '#6B7280', marginTop: 4 }}>{total.toLocaleString()} emails total</p>
          </div>
          <button
            onClick={fetchLogs}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                     border: '1px solid #D1D5DB', borderRadius: 8, background: 'white',
                     cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
          >
            <ArrowPathIcon style={{ width: 16 }} /> Refresh
          </button>
        </div>

        {/* Email not configured banner */}
        {!emailConfigured && (
          <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <ExclamationTriangleIcon style={{ width: 22, color: '#D97706', flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#92400E', fontSize: 15, marginBottom: 4 }}>Email not configured</div>
                <div style={{ fontSize: 13, color: '#78350F', marginBottom: 10 }}>
                  Add your Resend API key to enable email sending. Get a free key at{' '}
                  <a href="https://resend.com" target="_blank" rel="noreferrer" style={{ color: '#D97706', fontWeight: 600 }}>resend.com</a>
                  {' '}— free plan includes 3,000 emails/month.
                </div>
                <button
                  onClick={() => setShowKeyPanel(!showKeyPanel)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#D97706', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  <KeyIcon style={{ width: 14 }} /> {showKeyPanel ? 'Cancel' : 'Add API Key'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* API Key Panel */}
        <div style={{ background: 'white', borderRadius: 12, padding: '20px 24px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showKeyPanel ? 16 : 0 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <KeyIcon style={{ width: 18, color: '#6B7280' }} /> Resend API Key
            </h3>
            <button
              onClick={() => setShowKeyPanel(!showKeyPanel)}
              style={{ fontSize: 13, color: '#00B4D8', background: 'none', border: '1px solid #00B4D8', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontWeight: 600 }}
            >
              {showKeyPanel ? 'Cancel' : 'Update Key'}
            </button>
          </div>
          {showKeyPanel && (
            <div>
              <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 12px' }}>
                Get your free API key at <a href="https://resend.com" target="_blank" rel="noreferrer" style={{ color: '#00B4D8', fontWeight: 600 }}>resend.com</a>.
                Free plan includes 3,000 emails/month. Paste your key below — it will be saved to the server .env file.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  value={apiKeyInput}
                  onChange={e => setApiKeyInput(e.target.value)}
                  placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'monospace' }}
                />
                <button
                  onClick={saveApiKey}
                  disabled={savingKey || !apiKeyInput.trim()}
                  style={{ padding: '10px 20px', background: savingKey ? '#9CA3AF' : '#00B4D8', color: 'white', border: 'none',
                           borderRadius: 8, cursor: savingKey ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  {savingKey ? 'Saving...' : 'Save Key'}
                </button>
              </div>
              <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>
                Note: After saving, restart the backend server for the key to take effect.
              </p>
            </div>
          )}
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 28 }}>
          {[
            { label: 'Total Emails', value: total, icon: EnvelopeIcon, color: '#0D1B2A' },
            { label: 'Sent', value: sentCount, icon: CheckCircleIcon, color: '#10B981' },
            { label: 'Failed', value: failedCount, icon: XCircleIcon, color: '#EF4444' },
          ].map(stat => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              style={{ background: 'white', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ background: stat.color + '15', borderRadius: 8, padding: 8 }}>
                  <stat.icon style={{ width: 22, color: stat.color }} />
                </div>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#0D1B2A' }}>{stat.value}</div>
                  <div style={{ fontSize: 13, color: '#6B7280' }}>{stat.label}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Test email panel */}
        <div style={{ background: 'white', borderRadius: 12, padding: '20px 24px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 28 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', margin: '0 0 14px' }}>
            Send Test Email
          </h3>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="recipient@example.com"
              style={{ flex: 1, padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: 8,
                       fontSize: 14, outline: 'none' }}
            />
            <button
              onClick={sendTest}
              disabled={sending || !testEmail}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                       background: sending ? '#9CA3AF' : '#00B4D8', color: 'white', border: 'none',
                       borderRadius: 8, cursor: sending ? 'not-allowed' : 'pointer',
                       fontSize: 14, fontWeight: 600 }}
            >
              <PaperAirplaneIcon style={{ width: 16 }} />
              {sending ? 'Sending...' : 'Send Test'}
            </button>
          </div>
          <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8, marginBottom: 0 }}>
            Sends a test email to verify the Resend API key is working correctly.
          </p>
        </div>

        {/* Filters */}
        <div style={{ background: 'white', borderRadius: 12, padding: '16px 24px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 20,
                      display: 'flex', gap: 12, alignItems: 'center' }}>
          <FunnelIcon style={{ width: 18, color: '#6B7280' }} />
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 14, outline: 'none' }}>
            <option value="">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
            style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 14, outline: 'none' }}>
            <option value="">All Types</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          {(statusFilter || typeFilter) && (
            <button onClick={() => { setStatusFilter(''); setTypeFilter(''); setPage(1); }}
              style={{ fontSize: 13, color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer' }}>
              Clear filters
            </button>
          )}
        </div>

        {/* Logs table */}
        <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                {['Type', 'Recipient', 'Subject', 'Status', 'Attempts', 'Date'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12,
                                       fontWeight: 600, color: '#6B7280', textTransform: 'uppercase',
                                       letterSpacing: '0.05em', borderBottom: '1px solid #F3F4F6' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
                  No emails logged yet. Send a test email above to get started.
                </td></tr>
              ) : logs.map(log => {
                const st = STATUS_MAP[log.status] || STATUS_MAP.pending;
                const StatusIcon = st.icon;
                return (
                  <tr key={log.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#0D1B2A',
                                     background: '#F0F7FA', padding: '3px 8px', borderRadius: 4 }}>
                        {TYPE_LABELS[log.email_type] || log.email_type}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: '#374151', maxWidth: 180,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.recipient}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280', maxWidth: 220,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.subject}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                                      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                      background: st.bg, color: st.color }}>
                        <StatusIcon style={{ width: 12 }} /> {st.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: '#374151', textAlign: 'center' }}>
                      {log.attempts}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#9CA3AF' }}>
                      {log.created_at ? new Date(log.created_at).toLocaleString('en-GB', {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                      }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {total > 50 && (
            <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'center', gap: 8,
                          borderTop: '1px solid #F3F4F6' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '6px 14px', border: '1px solid #E5E7EB', borderRadius: 6,
                          fontSize: 13, cursor: page === 1 ? 'not-allowed' : 'pointer',
                          color: page === 1 ? '#9CA3AF' : '#374151' }}>Previous</button>
              <span style={{ padding: '6px 14px', fontSize: 13, color: '#6B7280' }}>
                Page {page} of {Math.ceil(total / 50)}
              </span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 50)}
                style={{ padding: '6px 14px', border: '1px solid #E5E7EB', borderRadius: 6,
                          fontSize: 13, cursor: page >= Math.ceil(total / 50) ? 'not-allowed' : 'pointer',
                          color: page >= Math.ceil(total / 50) ? '#9CA3AF' : '#374151' }}>Next</button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
